// THE ROW'S WORDS AS PLAIN TEXT (UI rebuild batch 2, Money movements). components/MovementTable's `Description` draws the
// same words with each party as a link; a v2 register row is itself the door to its receipt (the kit's Register wraps the
// row's first cell, and the phone card whole, in one button), and a link inside a button is not a control anyone can
// press. So the register says the words here, plain, and the party links live on the receipt. ONE SHAPE PER KIND, the
// same as Description's — counterpart first, never a figure — built from the same helpers, so the two cannot drift on
// what a kind is called: change one, change the other.
import type { MovementLineRow, StatementLine } from '../../MovementTable';
import { costWords, isOpening, isProfitShare, railWords, swapWords, tidy } from '../../MovementTable';
import { foldAsset, movementKindLabel, partyName, shortReference } from '../../../lib/naming';
import { fmtDate, fmtTime, dateBucket } from '../../../lib/format';

const capitalise = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);

/** The counterpart-led description of one line (Description's wording, without links). */
export function describe(line: StatementLine): string {
  const m = line.m;
  switch (m.kind) {
    case 'money_in':
      return isOpening(m) ? 'Opening balance'
        : m.party ? `From ${partyName(m.party)}` : m.rail ? `Into ${railWords(m.rail)}` : '—';
    case 'money_out': {
      const cost = m.cost ? ` · ${costWords(m.cost.category)}` : '';
      return isOpening(m) ? 'Opening balance'
        : `${m.party ? `To ${partyName(m.party)}` : m.rail ? `Out of ${railWords(m.rail)}` : '—'}${cost}`;
    }
    case 'trade':
      return m.party ? partyName(m.party) : swapWords(m) ?? (m.rail ? `On ${railWords(m.rail)}` : '—');
    case 'p2p':
      return m.party ? `${partyName(m.party)}${m.counterparty ? ` → ${partyName(m.counterparty)}` : ''}` : '—';
    case 'rail_move': {
      const feeOnly = !!m.cost && line.net !== null && line.net < 0
        && line.asset === foldAsset(m.asset)
        && tidy(Math.abs(line.net)) === tidy(Math.abs(m.cost.amount));
      if (line.net !== null && line.net > 0) return m.rail ? `From ${railWords(m.rail)}` : '—';
      if (m.counter_rail) return `To ${railWords(m.counter_rail)}${feeOnly && m.cost ? ` · ${costWords(m.cost.category)}` : ''}`;
      return m.rail ? `From ${railWords(m.rail)}` : '—';
    }
    case 'expense':
      return capitalise(costWords(m.cost?.category));
    case 'owner':
      return m.direction === 'in' ? 'Owner put in' : 'Owner took out';
    case 'adjustment': {
      const what = isOpening(m) ? 'Opening balance'
        : isProfitShare(m) ? 'Profit share'
        : m.direction === 'in' ? 'Wrote in' : 'Wrote off';
      return m.party ? `${what} · ${partyName(m.party)}` : what;
    }
    default:
      return movementKindLabel(m.kind);
  }
}

/** The row's head line — the transaction in its own head asset (what `Description` is handed for a journal row). */
export function lead(m: MovementLineRow): StatementLine {
  return { key: m.txn_id, m, asset: foldAsset(m.asset), net: null, running: null };
}

/** The state, in a word, ahead of the description — as the statement says it. Null for a live entry. */
export function stateWord(m: MovementLineRow): string | null {
  return m.state === 'reversal' ? 'Reversal' : m.state === 'reverted' ? 'Reversed' : null;
}

/** Dubai time: the clock alone today, the day and the clock before that (the register has no day rows). */
export function whenWords(m: MovementLineRow): string {
  if (!m.at) return 'Undated';
  return dateBucket(m.at) === 'today' ? fmtTime(m.at) : fmtDate(m.at);
}

/** The deal a row belongs to: its own leg's (`deal_id`), or the trade a sub-broker's money leg is linked to
 *  (`linked_deal_id`). For NAMING and OPENING the deal only — the Reverse door reads the row's own `deal_id` (canReverse),
 *  so a linked transfer stays reversible, exactly as on today's screen. */
export function dealOf(m: MovementLineRow): string | null {
  return m.deal_id ?? m.linked_deal_id ?? null;
}

/** THE ROW'S NAME, as Astra's register leads with it: the party itself on money in or out and on a trade (the direction
 *  word stands beside it, so "From" / "To" would say it twice) — a cost inside a payment still named after it; every
 *  other kind, and an opening balance, keeps describe()'s counterpart-led words. */
export function rowName(line: StatementLine): string {
  const m = line.m;
  if (m.party && !isOpening(m) && (m.kind === 'money_in' || m.kind === 'money_out' || m.kind === 'trade')) {
    return `${partyName(m.party)}${m.kind === 'money_out' && m.cost ? ` · ${costWords(m.cost.category)}` : ''}`;
  }
  return describe(line);
}

/** The second line under the name: the short reference (a deal's id, else the entry's) and the Dubai time — the phone's card,
 *  which has no columns. */
export function refTime(m: MovementLineRow): string {
  return [refOnly(m), whenWords(m)].filter(Boolean).join(' · ');
}

/** The laptop's second line: the short reference alone — the time has the When column (his "date and time everything in
 *  1st column", 1 Nov 2026). */
export function refOnly(m: MovementLineRow): string {
  const ref = m.deal_id ?? m.reference;
  return ref ? shortReference(ref) : '';
}
